import keyBy from 'lodash/keyBy';
import {
  IntegrationAuthType,
  IntegrationFormOption,
  IntegrationOption,
  IntegrationPlatform,
} from './types';

/** The breadcrumb configuration for Apps & Websites */
export const APPS_AND_WEBSITES_BREADCRUMB = {
  content: 'Apps and Websites',
  href: '/integrations/mobileApps',
};

export const ASSIGNED_ANDROID_INTEGRATION_OPTIONS: IntegrationOption[] = [
  { type: IntegrationPlatform.GCM, name: 'Firebase' },
];
export const ASSIGNED_IOS_INTEGRATION_OPTIONS: IntegrationOption[] = [
  { type: IntegrationPlatform.APNS, value: IntegrationPlatform.GCM, name: 'Apple' },
  { type: IntegrationPlatform.APNS_SANDBOX, name: 'Apple Sandbox' },
  { type: IntegrationPlatform.GCM, name: 'Firebase' },
];
export const ASSIGNED_IOS_INTEGRATION_OPTIONS_WITHOUT_FIREBASE: IntegrationOption[] = [
  { type: IntegrationPlatform.APNS, value: IntegrationPlatform.GCM, name: 'Apple' },
  { type: IntegrationPlatform.APNS_SANDBOX, name: 'Apple Sandbox' },
];

export const INTEGRATIONS_OPTIONS = keyBy(ASSIGNED_IOS_INTEGRATION_OPTIONS, 'type');

const FIREBASE_FORM_CONTENT = {
  name: 'Firebase',
  description: 'Never expires. One key needed for each app.',
  id: IntegrationAuthType.FIREBASE_JSON,
  inputs: {
    osNotifications: {
      id: 'osNotifications',
      label: 'Use OS notifications',
      placeholder: 'Render notifications with the Iterable SDK.',
    },
    privateKey: {
      id: 'privateKey',
      label: 'API key',
      placeholder: 'Enter GCM or FCM API key',
    },
  },
};

const P8_FORM_CONTENT = {
  name: '.p8 auth key',
  description: 'Never expires. One auth key for all apps and environments.',
  id: IntegrationAuthType.P8,
  inputs: {
    p8File: {
      accept: '.p8',
      acceptMimeType: 'application/pkcs8',
      id: 'p8File',
    },
    keyID: {
      label: 'Key ID',
      id: 'keyID',
    },
    teamID: {
      label: 'Team ID',
      id: 'teamID',
    },
    bundleID: {
      label: 'Bundle ID',
      id: 'bundleID',
    },
  },
};

const P12_FORM_CONTENT = {
  name: '.p12 certificate',
  description: 'Expires every year. One certificate needed for each app and environment.',
  id: IntegrationAuthType.P12,
  inputs: {
    p12file: {
      accept: '.p12',
      acceptMimeType: 'application/x-pkcs12',
      id: 'p12file',
      label: 'Select file for .p12 upload',
    },
    p12password: {
      label: 'Password',
      id: 'p12password',
      placeholder: 'Enter value',
      placeholder2: 'Password for the .p12 file',
    },
  },
};

const PEM_FORM_CONTENT = {
  name: 'PEM file',
  description: 'Expires every year. One certificate needed for each app and environment.',
  id: IntegrationAuthType.PEM,
  inputs: {
    certificate: {
      id: 'certificate',
      label: 'Push certificate',
      placeholder:
        'Enter push certificate. Looks like: -----BEGIN CERTIFICATE-----CERTIFICATE CONTENT-----END CERTIFICATE-----',
    },
    privateKey: {
      id: 'privateKey',
      label: 'RSA private key',
      placeholder:
        'Enter RSA private key. Looks like: -----BEGIN RSA PRIVATE KEY-----PRIVATE KEY CONTENT-----END RSA PRIVATE KEY-----',
    },
  },
};

export const INTEGRATION_FORM_CONTENT = {
  groupLabel: 'Select the configuration to use for your sends.',
  authMethods: {
    [IntegrationAuthType.FIREBASE_JSON]: FIREBASE_FORM_CONTENT,
    [IntegrationAuthType.P8]: P8_FORM_CONTENT,
    [IntegrationAuthType.P12]: P12_FORM_CONTENT,
    [IntegrationAuthType.PEM]: PEM_FORM_CONTENT,
  },
  environments: {
    id: 'integration-environments',
    label: 'Environment',
    prod: { id: 'env-prod', name: 'Production', value: IntegrationPlatform.APNS },
    sandbox: { id: 'env-sandbox', name: 'Sandbox', value: IntegrationPlatform.APNS_SANDBOX },
  },
};

export const INTEGRATION_FORM_IOS_OPTIONS: IntegrationFormOption[] = [
  {
    id: P8_FORM_CONTENT.id,
    name: P8_FORM_CONTENT.name,
    value: P8_FORM_CONTENT.id,
    description: P8_FORM_CONTENT.description,
  },
  {
    id: P12_FORM_CONTENT.id,
    name: P12_FORM_CONTENT.name,
    value: P12_FORM_CONTENT.id,
    description: P12_FORM_CONTENT.description,
  },
  {
    id: PEM_FORM_CONTENT.id,
    name: PEM_FORM_CONTENT.name,
    value: PEM_FORM_CONTENT.id,
    description: PEM_FORM_CONTENT.description,
  },
  {
    id: FIREBASE_FORM_CONTENT.id,
    name: FIREBASE_FORM_CONTENT.name,
    value: FIREBASE_FORM_CONTENT.id,
    description: FIREBASE_FORM_CONTENT.description,
  },
];

export const ADDING_APNS_ERROR =
  'Update failed. An existing Firebase iOS credential already exists. Please delete the existing Firebase iOS credential and try again.';
export const ADDING_FIREBASE_ERROR =
  'Update failed. An existing iOS credential already exists. Please delete the existing iOS credential and try again.';

export const P8_LEARN_MORE_URL = 'https://support.iterable.com/hc/articles/115000315806';
export const FIREBASE_API_UPGRADE_DOCS_LINK =
  'https://support.iterable.com/hc/articles/22626619859860#migrating-from-legacy-firebase-to-firebase-http-v1';
export const FIREBASE_DEPRECATION_TOOLTIP_CONTENT =
  'Update your Firebase credentials by June 20, 2024, to continue in sending Android push in Iterable.';

export const EMPTY_STATE_CONTENT = {
  PRIMARY_TEXT: 'Get started by adding a push credential',
  SECONDARY_TEXT: `To get started you'll need a .p8 Auth Key, .p12 Certificate, or PEM file.`,
};

export const EMPTY_P8_FORM_DATA = { p8File: '', keyID: '', teamID: '', bundleID: '' };
export const EMPTY_P12_FORM_DATA = { p12file: '', p12password: '' };

export const DEFAULT_MOBILE_APP_INTEGRATIONS = {
  [IntegrationPlatform.GCM]: { platform: IntegrationPlatform.GCM },
  [IntegrationPlatform.APNS]: { platform: IntegrationPlatform.APNS },
  [IntegrationPlatform.APNS_SANDBOX]: { platform: IntegrationPlatform.APNS_SANDBOX },
};

export const DEFAULT_CREATE_INTEGRATION_MODAL_STATE = {
  androidUseOsNotifications: false,
  certificate: '',
  name: '',
  p12file: undefined,
  p12password: '',
  platform: IntegrationPlatform.APNS,
  privateKey: '',
};

export const TOAST_ACTIONS = {
  fetchMobileApps: 'fetchMobileApps',
  fetchMobileAppInfo: 'fetchMobileAppInfo',
  createMobileApp: 'createMobileApp',
  updateMobileApp: 'updateMobileApp',
  deleteMobileApp: 'deleteMobileApp',
  fetchIntegrations: 'fetchIntegrations',
  createIntegration: 'createIntegration',
  assignIntegration: 'assignIntegration',
  updateIntegration: 'updateIntegration',
  deleteIntegration: 'deleteIntegration',
  testPush: 'testPush',
};
